// Design keyboard map (DRAW_PLAN §10): undo/redo, save, fit/100%, and tool keys. Disabled while typing.
import { useEffect } from 'react';
import { useDesign, saveDesignNow } from '../state';
import { fitToWindow, zoomTo } from './DesignCanvas';
import { TOOLS, useDesignUi } from './designUi';
import { currentList } from '../render/instance';
import { defaultFillStroke, swapFillStroke } from './FillStrokeBoxes';
import { setTargetColor } from './look';
import { averageSelected, compound, deleteSelectedAnchors, joinPaths } from './pathActions';
import { toolFor } from '../tools/registry';
import { arrangeNodes, groupNodes, removeNodes, transformNodes, ungroupNodes } from '../commands/commands';
import { findNode } from '../model/tree';
import type { Mat2D } from '../model/types';

let nudges = 0;
/** Moves the selection by (dx, dy) points; a run of arrow presses is one undo step. */
function nudge(dx: number, dy: number) {
  const st = useDesign.getState();
  const sel = useDesignUi.getState().selection;
  if (!st.doc || !sel.length) return;
  const next = new Map<string, Mat2D>();
  for (const id of sel) {
    const f = findNode(st.doc, id);
    if (f)
      next.set(id, [
        f.node.transform[0],
        f.node.transform[1],
        f.node.transform[2],
        f.node.transform[3],
        f.node.transform[4] + dx,
        f.node.transform[5] + dy,
      ]);
  }
  st.run(transformNodes(next, `nudge-${nudges}`));
}

const typing = (t: EventTarget | null) =>
  t instanceof HTMLInputElement ||
  t instanceof HTMLTextAreaElement ||
  t instanceof HTMLSelectElement ||
  (t instanceof HTMLElement && t.isContentEditable);

export function useDesignKeyboard() {
  useEffect(() => {
    const onDown = (e: KeyboardEvent) => {
      if (typing(e.target)) return;
      const st = useDesign.getState();
      if (!st.doc) return;
      const mod = e.ctrlKey || e.metaKey;
      const k = e.key.toLowerCase();
      if (mod && k === 'z') {
        (e.shiftKey ? st.redo : st.undo)();
        e.preventDefault();
      } else if (mod && k === 'y') {
        st.redo();
        e.preventDefault();
      } else if (mod && k === 's') {
        void saveDesignNow();
        e.preventDefault();
      } else if (mod && k === '0') {
        fitToWindow();
        e.preventDefault();
      } else if (mod && k === '1') {
        zoomTo(1);
        e.preventDefault();
      } else if (mod && (e.code === 'Quote' || e.code === 'Semicolon' || k === 'u')) {
        // View toggles: Ctrl+' grid, Ctrl+Shift+' snap to grid, Ctrl+Alt+' snap to pixels, Ctrl+; guides,
        // Ctrl+Alt+; snapping on/off, Ctrl+U smart guides.
        const ui = useDesignUi.getState();
        if (k === 'u') useDesignUi.setState({ snap: { ...ui.snap, smart: !ui.snap.smart } });
        else if (e.code === 'Quote') {
          if (e.shiftKey) useDesignUi.setState({ snap: { ...ui.snap, grid: !ui.snap.grid } });
          else if (e.altKey) useDesignUi.setState({ snap: { ...ui.snap, pixel: !ui.snap.pixel } });
          else useDesignUi.setState({ showGrid: !ui.showGrid });
        } else if (e.altKey) useDesignUi.setState({ snap: { ...ui.snap, enabled: !ui.snap.enabled } });
        else useDesignUi.setState({ showGuides: !ui.showGuides });
        e.preventDefault();
      } else if (mod && k === 'j') {
        if (e.altKey) averageSelected();
        else joinPaths();
        e.preventDefault();
      } else if (mod && (e.code === 'Digit8' || k === '8' || k === '*')) {
        compound(!(e.altKey && e.shiftKey));
        e.preventDefault();
      } else if (
        !mod &&
        (e.key === 'Enter' || e.key === 'Escape') &&
        ['pen', 'curvature'].includes(useDesignUi.getState().tool)
      ) {
        toolFor(useDesignUi.getState().tool)?.cancel?.();
        useDesign.setState({ version: useDesign.getState().version + 1 });
        e.preventDefault();
      } else if (mod && k === 'a') {
        useDesignUi.getState().set({
          selection: [
            ...new Set(
              currentList()
                .items.filter((i) => !i.hidden && !i.locked)
                .map((i) => i.topId),
            ),
          ],
        });
        e.preventDefault();
      } else if (mod && k === 'g') {
        const sel = useDesignUi.getState().selection;
        if (sel.length) {
          const out: string[] = [];
          st.run(e.shiftKey ? ungroupNodes(sel, out) : groupNodes(sel, out));
          useDesignUi.getState().set({ selection: out.length ? out : sel });
        }
        e.preventDefault();
      } else if (mod && (e.key === ']' || e.key === '[' || e.key === '}' || e.key === '{')) {
        const sel = useDesignUi.getState().selection;
        const up = e.key === ']' || e.key === '}';
        if (sel.length)
          st.run(arrangeNodes(sel, e.shiftKey ? (up ? 'front' : 'back') : up ? 'forward' : 'backward'));
        e.preventDefault();
      } else if (
        !mod &&
        (e.key === 'Delete' || e.key === 'Backspace') &&
        ['direct', 'lasso'].includes(useDesignUi.getState().tool) &&
        deleteSelectedAnchors()
      ) {
        e.preventDefault();
      } else if (!mod && (e.key === 'Delete' || e.key === 'Backspace')) {
        const sel = useDesignUi.getState().selection;
        if (sel.length) {
          st.run(removeNodes(sel));
          useDesignUi.getState().set({ selection: [] });
        }
        e.preventDefault();
      } else if (!mod && !e.altKey && (k === 'x' || k === 'd' || e.key === '/')) {
        if (k === 'x' && e.shiftKey) swapFillStroke();
        else if (k === 'x')
          useDesignUi
            .getState()
            .set({ target: useDesignUi.getState().target === 'fill' ? 'stroke' : 'fill' });
        else if (k === 'd') defaultFillStroke();
        else setTargetColor(null);
        e.preventDefault();
      } else if (!mod && e.key === 'Escape') {
        useDesignUi.getState().set({ selection: [] });
      } else if (!mod && e.key.startsWith('Arrow')) {
        const step = e.shiftKey ? 10 : 1;
        const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
        const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
        nudge(dx, dy);
        e.preventDefault();
      } else if (!mod && !e.altKey) {
        const combo =
          e.shiftKey && e.key.length === 1
            ? 'Shift+' + e.key.toUpperCase()
            : e.key.length === 1
              ? e.key.toUpperCase()
              : e.key;
        const tool = TOOLS.find((t) => t.ready && t.key && (t.key === combo || t.key === e.key));
        if (tool) {
          useDesignUi.getState().set({ tool: tool.id });
          e.preventDefault();
        }
      }
    };
    const onUp = (e: KeyboardEvent) => {
      if (e.key.startsWith('Arrow')) {
        nudges++;
        useDesign.getState().endGesture();
      }
    };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
    };
  }, []);
}
